<script lang="ts">
	import { onMount } from 'svelte';
	import { appStore } from '../stores/appStore';
	import { getTokenStorage } from '../utils/localStorage';
	import { REST_URL } from '../utils/config';
	import { AppApiClient } from '../api_contracts/app.client';
	import { UserProfile } from '../api_contracts/app';
	import RepositoryMembers from './RepositoryMembers.svelte';

	// Props
	let { repositoryName, ownerId, onSettingsUpdate }: {
		repositoryName: string;
		ownerId: string;
		onSettingsUpdate?: () => void;
	} = $props();

	// State
	let client: AppApiClient | null = $state(null);
	let profile: UserProfile | null = $state(null);
	let token: string = $state('');

	// Subscribe to store
	appStore.subscribe((state) => {
		profile = state.profile;
		client = state.client;
		token = state.token;
	});

	// Data types
	interface AccessToken {
		id: string;
		name: string;
		token: string;
		scopes: string[];
		createdAt: string;
		lastUsed: string | null;
	}

	interface Webhook {
		id: string;
		url: string;
		events: string[];
		active: boolean;
		secret: string;
		createdAt: string;
	}

	interface Member {
		id: string;
		userId: string;
		username: string;
		email: string;
		role: string;
		avatar: string;
		addedAt: string;
	}

	interface Secret {
		id: string;
		name: string;
		createdAt: string;
		updatedAt: string;
	}

	// Data arrays
	let accessTokens = $state<AccessToken[]>([]);
	let webhooks = $state<Webhook[]>([]);
	let members = $state<Member[]>([]);
	let secrets = $state<Secret[]>([]);

	// Repository settings state
	let repoName = $state('');
	let repoDescription = $state('');
	let repoVisibility = $state<'public' | 'private'>('public');
	let repoAvatar = $state<string | null>(null);
	let isArchived = $state(false);
	let selectedSection = $state('general');

	// Loading and error states
	let loading = $state(true);
	let error = $state('');
	let saving = $state(false);

	// Usage stats
	let usageStats = $state({
		storageUsed: 0,
		storageLimit: 1000,
		bandwidthUsed: 0,
		bandwidthLimit: 10,
		gitLfsStorage: 0,
		codeRepository: 0,
		buildArtifacts: 0
	});

	// Modal states
	let showTokenModal = $state(false);
	let newTokenName = $state('');
	let selectedScopes = $state<string[]>([]);

	let showWebhookModal = $state(false);
	let newWebhookUrl = $state('');
	let selectedEvents = $state<string[]>([]);

	let showMemberModal = $state(false);
	let newMemberEmail = $state('');
	let newMemberRole = $state<'admin' | 'write' | 'read'>('read');

	let showSecretModal = $state(false);
	let newSecretName = $state('');
	let newSecretValue = $state('');
	let editingSecretId = $state<string | null>(null);
	let isEditingSecret = $state(false);

	const availableScopes = [
		{ value: 'read:repo', label: 'Read repository' },
		{ value: 'write:repo', label: 'Write repository' },
		{ value: 'delete:repo', label: 'Delete repository' },
		{ value: 'admin:repo', label: 'Admin repository' }
	];

	const availableEvents = [
		{ value: 'push', label: 'Push events' },
		{ value: 'pull_request', label: 'Pull request events' },
		{ value: 'issues', label: 'Issue events' },
		{ value: 'release', label: 'Release events' },
		{ value: 'deploy', label: 'Deployment events' }
	];

	// Fetch repository settings
	async function fetchRepositorySettings() {
		if (!client || !token) return;
		try {
			const result = await client.getRepositorySettings({
				token,
				ownerId,
				repositoryName
			});
			if (result.response.successFull) {
				repoName = result.response.name || '';
				repoDescription = result.response.description || '';
				repoVisibility = (result.response.visibility as 'public' | 'private') || 'public';
				repoAvatar = result.response.avatar || null;
				isArchived = result.response.isArchived || false;
			}
		} catch (e: any) {
			console.error('Error fetching settings:', e);
		}
	}

	// Update repository settings
	async function saveRepositorySettings() {
		if (!client || !token) return;
		try {
			saving = true;
			const result = await client.updateRepositorySettings({
				token,
				ownerId,
				repositoryName,
				newName: repoName,
				newDescription: repoDescription,
				avatar: repoAvatar || ''
			});
			if (result.response.successFull) {
				alert('Settings saved successfully!');
			} else {
				alert('Failed to save settings: ' + result.response.reason);
			}
		} catch (e: any) {
			alert('Error saving settings: ' + e.message);
		} finally {
			saving = false;
		}
	}

	// Update visibility
	async function updateVisibility() {
		if (!client || !token) return;
		try {
			saving = true;
			const result = await client.updateRepositoryVisibility({
				token,
				ownerId,
				repositoryName,
				visibility: repoVisibility
			});
			if (result.response.successFull) {
				alert('Visibility updated successfully!');
				// Notify parent component to refresh
				if (onSettingsUpdate) {
					onSettingsUpdate();
				}
			} else {
				alert('Failed to update visibility: ' + result.response.reason);
			}
		} catch (e: any) {
			alert('Error updating visibility: ' + e.message);
		} finally {
			saving = false;
		}
	}

	// Access Tokens
	async function fetchAccessTokens() {
		if (!client || !token) return;
		try {
			const result = await client.getAccessTokens({ token, ownerId, repositoryName });
			if (result.response.successFull) {
				accessTokens = (result.response.tokens || []).map((t: any) => ({
					id: t.id,
					name: t.name,
					token: t.token,
					scopes: t.scopes || [],
					createdAt: t.createdAt ? new Date(Number(t.createdAt.seconds) * 1000).toLocaleDateString() : '',
					lastUsed: t.lastUsedAt ? new Date(Number(t.lastUsedAt.seconds) * 1000).toLocaleDateString() : null
				}));
			}
		} catch (e: any) {
			console.error('Error fetching tokens:', e);
		}
	}

	async function generateToken() {
		if (!newTokenName || !client || !token) return;
		try {
			const result = await client.createAccessToken({
				token,
				ownerId,
				repositoryName,
				name: newTokenName,
				scopes: selectedScopes
			});
			if (result.response.successFull) {
				showTokenModal = false;
				newTokenName = '';
				selectedScopes = [];
				fetchAccessTokens();
			}
		} catch (e: any) {
			alert('Error creating token: ' + e.message);
		}
	}

	async function deleteToken(id: string) {
		if (!client || !token) return;
		try {
			await client.deleteAccessToken({ token, ownerId, repositoryName, tokenId: id });
			fetchAccessTokens();
		} catch (e: any) {
			alert('Error deleting token: ' + e.message);
		}
	}

	// Webhooks
	async function fetchWebhooks() {
		if (!client || !token) return;
		try {
			console.log('Fetching webhooks for:', { ownerId, repositoryName });
			const result = await client.getWebhooks({ token, ownerId, repositoryName });
			console.log('Webhooks API response:', result);
			if (result.response.successFull) {
				console.log('Raw webhooks data:', result.response.webhooks);
				webhooks = (result.response.webhooks || []).map((w: any) => ({
					id: w.id,
					url: w.url,
					events: w.events || [],
					active: w.active,
					secret: w.secret,
					createdAt: w.createdAt ? new Date(Number(w.createdAt.seconds) * 1000).toLocaleDateString() : ''
				}));
				console.log('Mapped webhooks:', webhooks);
			} else {
				console.error('Failed to fetch webhooks:', result.response.reason);
			}
		} catch (e: any) {
			console.error('Error fetching webhooks:', e);
		}
	}

	async function addWebhook() {
		if (!newWebhookUrl || selectedEvents.length === 0 || !client || !token) return;
		try {
			console.log('Creating webhook with:', { url: newWebhookUrl, events: selectedEvents, ownerId, repositoryName });
			const result = await client.createWebhook({
				token,
				ownerId,
				repositoryName,
				url: newWebhookUrl,
				events: selectedEvents
			});
			console.log('Create webhook response:', result);
			if (result.response.successFull) {
				showWebhookModal = false;
				newWebhookUrl = '';
				selectedEvents = [];
				console.log('Fetching webhooks after creation...');
				await fetchWebhooks();
				alert('Webhook created successfully!');
			} else {
				alert('Failed to create webhook: ' + result.response.reason);
			}
		} catch (e: any) {
			console.error('Create webhook error:', e);
			alert('Error creating webhook: ' + e.message);
		}
	}

	async function deleteWebhook(id: string) {
		if (!client || !token) return;
		try {
			await client.deleteWebhook({ token, ownerId, repositoryName, webhookId: id });
			fetchWebhooks();
		} catch (e: any) {
			alert('Error deleting webhook: ' + e.message);
		}
	}

	// Members
	async function fetchMembers() {
		if (!client || !token) return;
		try {
			const result = await client.getRepositoryMembers({ token, ownerId, repositoryName });
			if (result.response.successFull) {
				members = (result.response.members || []).map((m: any) => ({
					id: m.id,
					userId: m.userId,
					username: m.username,
					email: m.email,
					role: m.role,
					avatar: m.avatar || '',
					addedAt: m.addedAt ? new Date(Number(m.addedAt.seconds) * 1000).toLocaleDateString() : ''
				}));
			}
		} catch (e: any) {
			console.error('Error fetching members:', e);
		}
	}

	async function addMember() {
		if (!newMemberEmail || !client || !token) return;
		try {
			const result = await client.addRepositoryMember({
				token,
				ownerId,
				repositoryName,
				userEmail: newMemberEmail,
				role: newMemberRole
			});
			if (result.response.successFull) {
				showMemberModal = false;
				newMemberEmail = '';
				newMemberRole = 'read';
				fetchMembers();
			} else {
				alert('Failed to add member: ' + result.response.reason);
			}
		} catch (e: any) {
			alert('Error adding member: ' + e.message);
		}
	}

	async function removeMember(id: string) {
		if (!client || !token) return;
		try {
			await client.removeRepositoryMember({ token, ownerId, repositoryName, memberId: id });
			fetchMembers();
		} catch (e: any) {
			alert('Error removing member: ' + e.message);
		}
	}

	// Secrets
	async function fetchSecrets() {
		if (!client || !token) return;
		try {
			const result = await client.getRepositorySecrets({ token, ownerId, repositoryName });
			if (result.response.successFull) {
				secrets = (result.response.secrets || []).map((s: any) => ({
					id: s.id,
					name: s.name,
					createdAt: s.createdAt ? new Date(Number(s.createdAt.seconds) * 1000).toLocaleDateString() : '',
					updatedAt: s.updatedAt ? new Date(Number(s.updatedAt.seconds) * 1000).toLocaleDateString() : ''
				}));
			}
		} catch (e: any) {
			console.error('Error fetching secrets:', e);
		}
	}

	async function addSecret() {
		if (!newSecretName || !newSecretValue || !client || !token) return;
		try {
			const result = await client.createRepositorySecret({
				token,
				ownerId,
				repositoryName,
				name: newSecretName,
				value: newSecretValue
			});
			if (result.response.successFull) {
				showSecretModal = false;
				newSecretName = '';
				newSecretValue = '';
				isEditingSecret = false;
				editingSecretId = null;
				fetchSecrets();
				alert('Secret created successfully!');
			} else {
				alert('Failed to create secret: ' + result.response.reason);
			}
		} catch (e: any) {
			alert('Error creating secret: ' + e.message);
		}
	}

	function openUpdateSecretModal(secret: Secret) {
		editingSecretId = secret.id;
		newSecretName = secret.name;
		newSecretValue = '';
		isEditingSecret = true;
		showSecretModal = true;
	}

	async function updateSecret() {
		if (!editingSecretId || !newSecretValue || !client || !token) return;
		try {
			const result = await client.updateRepositorySecret({
				token,
				ownerId,
				repositoryName,
				secretId: editingSecretId,
				value: newSecretValue
			});
			if (result.response.successFull) {
				showSecretModal = false;
				newSecretName = '';
				newSecretValue = '';
				isEditingSecret = false;
				editingSecretId = null;
				fetchSecrets();
				alert('Secret updated successfully!');
			} else {
				alert('Failed to update secret: ' + result.response.reason);
			}
		} catch (e: any) {
			alert('Error updating secret: ' + e.message);
		}
	}

	async function deleteSecret(id: string) {
		if (!client || !token) return;
		if (!confirm('Are you sure you want to delete this secret?')) return;
		try {
			await client.deleteRepositorySecret({ token, ownerId, repositoryName, secretId: id });
			fetchSecrets();
			alert('Secret deleted successfully!');
		} catch (e: any) {
			alert('Error deleting secret: ' + e.message);
		}
	}

	// Usage stats
	async function fetchUsageStats() {
		if (!client || !token) return;
		try {
			const result = await client.getRepositoryUsage({ token, ownerId, repositoryName });
			if (result.response.successFull) {
				usageStats = {
					storageUsed: result.response.storageUsedMb || 0,
					storageLimit: result.response.storageLimitMb || 1000,
					bandwidthUsed: result.response.bandwidthUsedGb || 0,
					bandwidthLimit: result.response.bandwidthLimitGb || 10,
					gitLfsStorage: result.response.gitLfsStorageMb || 0,
					codeRepository: result.response.codeRepositoryMb || 0,
					buildArtifacts: result.response.buildArtifactsMb || 0
				};
			}
		} catch (e: any) {
			console.error('Error fetching usage:', e);
		}
	}

	// Danger zone actions
	async function archiveRepository() {
		if (!confirm('Are you sure you want to archive this repository? It will become read-only.')) return;
		if (!client || !token) return;
		try {
			const result = await client.archiveRepository({ token, ownerId, repositoryName });
			if (result.response.successFull) {
				alert('Repository archived successfully');
				fetchRepositorySettings();
			}
		} catch (e: any) {
			alert('Error archiving repository: ' + e.message);
		}
	}

	async function transferRepository() {
		const newOwner = prompt('Enter the username or organization to transfer this repository to:');
		if (!newOwner) return;
		if (!confirm(`Are you sure you want to transfer this repository to ${newOwner}?`)) return;
		if (!client || !token) return;
		try {
			const result = await client.transferRepository({
				token,
				ownerId,
				repositoryName,
				newOwnerId: newOwner
			});
			if (result.response.successFull) {
				alert('Repository transfer initiated successfully');
				window.location.href = '/';
			}
		} catch (e: any) {
			alert('Error transferring repository: ' + e.message);
		}
	}

	async function deleteRepository() {
		const confirmation = prompt(
			`This action cannot be undone. Type "${repoName}" to confirm deletion:`
		);
		if (confirmation !== repoName) {
			alert('Repository name does not match. Deletion cancelled.');
			return;
		}
		if (!client || !token) return;
		try {
			const result = await client.deleteRepository({ token, ownerId, repositoryName });
			if (result.response.successFull) {
				alert('Repository deleted successfully');
				window.location.href = '/';
			}
		} catch (e: any) {
			alert('Error deleting repository: ' + e.message);
		}
	}

	// Utility functions
	const handleAvatarUpload = async (event: Event) => {
		const target = event.target as HTMLInputElement;
		if (!target.files || !target.files[0] || !client || !token) return;

		const file = target.files[0];

		// Validate file size (max 5MB)
		const maxSize = 5 * 1024 * 1024; // 5MB
		if (file.size > maxSize) {
			alert('File size exceeds 5MB limit');
			return;
		}

		// Validate file type
		const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
		if (!allowedTypes.includes(file.type)) {
			alert('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed');
			return;
		}

		try {
			// Create FormData
			const formData = new FormData();
			formData.append('avatar', file);
			formData.append('owner_id', ownerId);
			formData.append('repository_name', repositoryName);

			// Upload to server
			const response = await fetch(`${REST_URL}/api/v1/repository/avatar`, {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${token}`,
				},
				body: formData,
			});

			const data = await response.json();

			if (data.error) {
				alert('Failed to upload avatar: ' + data.message);
			} else {
				// Update local preview with uploaded URL
				repoAvatar = data.avatar_url;
				alert('Repository avatar uploaded successfully!');
				// Call onSettingsUpdate to refresh repository data
				if (onSettingsUpdate) {
					onSettingsUpdate();
				}
			}
		} catch (e: any) {
			console.error('Error uploading avatar:', e);
			alert('Error uploading avatar: ' + e.message);
		}
	};

	const handleKeyPress = (event: KeyboardEvent, section: string) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			selectedSection = section;
		}
	};

	// Initialize data on mount
	async function fetchRepositoryAvatar() {
		if (!client || !token) return;
		try {
			const result = await client.getRepositoryDetails({
				token,
				ownerId,
				repositoryName,
			});
			if (result.response.successFull && result.response.repository) {
				repoAvatar = result.response.repository.avatarUrl || null;
			}
		} catch (e: any) {
			console.error('Error fetching repository avatar:', e);
		}
	}

	onMount(() => {
		fetchRepositorySettings();
		fetchRepositoryAvatar();
		fetchAccessTokens();
		fetchWebhooks();
		fetchMembers();
		fetchSecrets();
		fetchUsageStats();
		loading = false;
	});
</script>

<div class="flex flex-col lg:flex-row gap-6">
	<!-- Sidebar Navigation -->
	<div class="w-full lg:w-64 flex-shrink-0">
		<nav class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 shadow-sm">
			<div
				role="button"
				tabindex="0"
				onclick={() => (selectedSection = 'general')}
				onkeydown={(e) => handleKeyPress(e, 'general')}
				class="group w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer {selectedSection === 'general'
					? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-orange-500/30'
					: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 hover:pl-4'}"
			>
				<div class="flex items-center gap-3">
					<svg class="w-5 h-5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
							clip-rule="evenodd"
						/>
					</svg>
					<span class="font-medium">General</span>
				</div>
			</div>
			<div
				role="button"
				tabindex="0"
				onclick={() => (selectedSection = 'visibility')}
				onkeydown={(e) => handleKeyPress(e, 'visibility')}
				class="group w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer {selectedSection === 'visibility'
					? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-orange-500/30'
					: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 hover:pl-4'}"
			>
				<div class="flex items-center gap-3">
					<svg class="w-5 h-5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 20 20">
						<path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
						<path
							fill-rule="evenodd"
							d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
							clip-rule="evenodd"
						/>
					</svg>
					<span class="font-medium">Visibility</span>
				</div>
			</div>
			<div
				role="button"
				tabindex="0"
				onclick={() => (selectedSection = 'access')}
				onkeydown={(e) => handleKeyPress(e, 'access')}
				class="group w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer {selectedSection === 'access'
					? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-orange-500/30'
					: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 hover:pl-4'}"
			>
				<div class="flex items-center gap-3">
					<svg class="w-5 h-5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z"
							clip-rule="evenodd"
						/>
					</svg>
					<span class="font-medium">Access Tokens</span>
				</div>
			</div>
			<div
				role="button"
				tabindex="0"
				onclick={() => (selectedSection = 'webhooks')}
				onkeydown={(e) => handleKeyPress(e, 'webhooks')}
				class="group w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer {selectedSection === 'webhooks'
					? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-orange-500/30'
					: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 hover:pl-4'}"
			>
				<div class="flex items-center gap-3">
					<svg class="w-5 h-5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 20 20">
						<path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
						<path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
					</svg>
					<span class="font-medium">Webhooks</span>
				</div>
			</div>
			<div
				role="button"
				tabindex="0"
				onclick={() => (selectedSection = 'members')}
				onkeydown={(e) => handleKeyPress(e, 'members')}
				class="group w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer {selectedSection === 'members'
					? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-orange-500/30'
					: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 hover:pl-4'}"
			>
				<div class="flex items-center gap-3">
					<svg class="w-5 h-5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 20 20">
						<path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
					</svg>
					<span class="font-medium">Members</span>
				</div>
			</div>
			<div
				role="button"
				tabindex="0"
				onclick={() => (selectedSection = 'secrets')}
				onkeydown={(e) => handleKeyPress(e, 'secrets')}
				class="group w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer {selectedSection === 'secrets'
					? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-orange-500/30'
					: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 hover:pl-4'}"
			>
				<div class="flex items-center gap-3">
					<svg class="w-5 h-5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
							clip-rule="evenodd"
						/>
					</svg>
					<span class="font-medium">Secrets</span>
				</div>
			</div>
			<div
				role="button"
				tabindex="0"
				onclick={() => (selectedSection = 'usage')}
				onkeydown={(e) => handleKeyPress(e, 'usage')}
				class="group w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer {selectedSection === 'usage'
					? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-orange-500/30'
					: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 hover:pl-4'}"
			>
				<div class="flex items-center gap-3">
					<svg class="w-5 h-5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 20 20">
						<path
							d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"
						/>
					</svg>
					<span class="font-medium">Usage</span>
				</div>
			</div>
			<div class="my-3 border-t border-gray-200 dark:border-gray-700"></div>
			<div
				role="button"
				tabindex="0"
				onclick={() => (selectedSection = 'danger')}
				onkeydown={(e) => handleKeyPress(e, 'danger')}
				class="group w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer {selectedSection === 'danger'
					? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md shadow-red-500/30'
					: 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:pl-4'}"
			>
				<div class="flex items-center gap-3">
					<svg class="w-5 h-5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
							clip-rule="evenodd"
						/>
					</svg>
					<span class="font-medium">Danger Zone</span>
				</div>
			</div>
		</nav>
	</div>

	<!-- Main Content -->
	<div class="flex-1 min-w-0">
		<!-- General Settings -->
		{#if selectedSection === 'general'}
			<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
				<div class="p-6 border-b border-gray-200 dark:border-gray-700">
					<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">General Settings</h2>
				</div>
				<div class="p-6 space-y-6">
					<!-- Repository Avatar -->
					<div>
						<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Repository Avatar
						</label>
						<div class="flex items-center gap-4">
							<div
								class="w-20 h-20 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden"
							>
								{#if repoAvatar}
									<img src={repoAvatar} alt="Repository avatar" class="w-full h-full object-cover" />
								{:else}
									<svg class="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
										<path
											fill-rule="evenodd"
											d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
											clip-rule="evenodd"
										/>
									</svg>
								{/if}
							</div>
							<div>
								<input
									type="file"
									id="avatar-upload"
									class="hidden"
									accept="image/*"
									onchange={handleAvatarUpload}
								/>
								<label
									for="avatar-upload"
									class="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer transition-colors inline-block"
								>
									Upload Avatar
								</label>
								<p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
									Recommended: Square image, at least 200x200px
								</p>
							</div>
						</div>
					</div>

					<!-- Repository Name -->
					<div>
						<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Repository Name
						</label>
						<input
							type="text"
							bind:value={repoName}
							class="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
						/>
						<p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
							Changing the repository name will break existing links and integrations
						</p>
					</div>

					<!-- Repository Description -->
					<div>
						<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Description
						</label>
						<textarea
							bind:value={repoDescription}
							rows="3"
							class="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
						></textarea>
					</div>

					<div class="pt-4">
						<button
							onclick={saveRepositorySettings}
							disabled={saving}
							class="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50"
						>
							{saving ? 'Saving...' : 'Save Changes'}
						</button>
					</div>
				</div>
			</div>
		{:else if selectedSection === 'visibility'}
			<!-- Visibility Settings -->
			<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
				<div class="p-6 border-b border-gray-200 dark:border-gray-700">
					<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
						Repository Visibility
					</h2>
					<p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
						Control who can see and access this repository
					</p>
				</div>
				<div class="p-6 space-y-6">
					<div class="space-y-4">
						<label
							class="flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-colors {repoVisibility ===
							'public'
								? 'border-orange-500 bg-orange-50 dark:bg-orange-900/10'
								: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}"
						>
							<input
								type="radio"
								name="visibility"
								value="public"
								bind:group={repoVisibility}
								class="mt-1"
							/>
							<div class="flex-1">
								<div class="flex items-center gap-2 mb-1">
									<svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20">
										<path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
										<path
											fill-rule="evenodd"
											d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
											clip-rule="evenodd"
										/>
									</svg>
									<span class="font-semibold text-gray-900 dark:text-gray-100">Public</span>
								</div>
								<p class="text-sm text-gray-600 dark:text-gray-400">
									Anyone on the internet can see this repository. You choose who can commit.
								</p>
							</div>
						</label>
						<label
							class="flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-colors {repoVisibility ===
							'private'
								? 'border-orange-500 bg-orange-50 dark:bg-orange-900/10'
								: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}"
						>
							<input
								type="radio"
								name="visibility"
								value="private"
								bind:group={repoVisibility}
								class="mt-1"
							/>
							<div class="flex-1">
								<div class="flex items-center gap-2 mb-1">
									<svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20">
										<path
											fill-rule="evenodd"
											d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
											clip-rule="evenodd"
										/>
									</svg>
									<span class="font-semibold text-gray-900 dark:text-gray-100">Private</span>
								</div>
								<p class="text-sm text-gray-600 dark:text-gray-400">
									You choose who can see and commit to this repository. Great for sensitive projects.
								</p>
							</div>
						</label>
					</div>

					<div class="pt-4 border-t border-gray-200 dark:border-gray-700">
						<button
							onclick={updateVisibility}
							disabled={saving}
							class="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50"
						>
							{saving ? 'Updating...' : 'Update Visibility'}
						</button>
					</div>
				</div>
			</div>
		{:else if selectedSection === 'access'}
			<!-- Access Tokens -->
			<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
				<div class="p-6 border-b border-gray-200 dark:border-gray-700">
					<div class="flex items-center justify-between">
						<div>
							<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Access Tokens</h2>
							<p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
								Tokens allow external applications to access this repository
							</p>
						</div>
						<button
							onclick={() => (showTokenModal = true)}
							class="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
						>
							Generate Token
						</button>
					</div>
				</div>
				<div class="divide-y divide-gray-200 dark:divide-gray-700">
					{#each accessTokens as token}
						<div class="p-6">
							<div class="flex items-start justify-between">
								<div class="flex-1">
									<h3 class="font-medium text-gray-900 dark:text-gray-100">{token.name}</h3>
									<code
										class="text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900 px-2 py-1 rounded mt-2 inline-block"
									>
										{token.token}
									</code>
									<div class="flex flex-wrap gap-2 mt-2">
										{#each token.scopes as scope}
											<span
												class="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded"
											>
												{scope}
											</span>
										{/each}
									</div>
									<p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
										Created {token.createdAt} • Last used {token.lastUsed || 'Never'}
									</p>
								</div>
								<button
									onclick={() => deleteToken(token.id)}
									class="text-red-600 hover:text-red-700 dark:text-red-400"
								>
									<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
										<path
											fill-rule="evenodd"
											d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
											clip-rule="evenodd"
										/>
									</svg>
								</button>
							</div>
						</div>
					{/each}
				</div>
			</div>
		{:else if selectedSection === 'webhooks'}
			<!-- Webhooks -->
			<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
				<div class="p-6 border-b border-gray-200 dark:border-gray-700">
					<div class="flex items-center justify-between">
						<div>
							<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Webhooks</h2>
							<p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
								Send notifications to external applications
							</p>
						</div>
						<button
							onclick={() => (showWebhookModal = true)}
							class="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
						>
							Add Webhook
						</button>
					</div>
				</div>
				<div class="divide-y divide-gray-200 dark:divide-gray-700">
					{#if webhooks.length === 0}
						<div class="p-6 text-center text-gray-500 dark:text-gray-400">
							<svg class="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
							</svg>
							<p class="text-lg font-medium mb-2">No webhooks configured</p>
							<p class="text-sm">Click "Add Webhook" to create your first webhook</p>
						</div>
					{/if}
					{#each webhooks as webhook}
						<div class="p-6">
							<div class="flex items-start justify-between">
								<div class="flex-1">
									<div class="flex items-center gap-2">
										<h3 class="font-medium text-gray-900 dark:text-gray-100">{webhook.url}</h3>
										<span
											class="px-2 py-0.5 rounded text-xs {webhook.active
												? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
												: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400'}"
										>
											{webhook.active ? 'Active' : 'Inactive'}
										</span>
									</div>
									<div class="flex flex-wrap gap-2 mt-2">
										{#each webhook.events as event}
											<span
												class="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded"
											>
												{event}
											</span>
										{/each}
									</div>
									<code
										class="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900 px-2 py-1 rounded mt-2 inline-block"
									>
										Secret: {webhook.secret}
									</code>
								</div>
								<button
									onclick={() => deleteWebhook(webhook.id)}
									class="text-red-600 hover:text-red-700 dark:text-red-400"
								>
									<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
										<path
											fill-rule="evenodd"
											d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
											clip-rule="evenodd"
										/>
									</svg>
								</button>
							</div>
						</div>
					{/each}
				</div>
			</div>
		{:else if selectedSection === 'members'}
			<!-- Members -->
			<RepositoryMembers {repositoryName} {ownerId} />
		{:else if selectedSection === 'secrets'}
			<!-- Secrets -->
			<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
				<div class="p-6 border-b border-gray-200 dark:border-gray-700">
					<div class="flex items-center justify-between">
						<div>
							<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Repository Secrets</h2>
							<p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
								Store sensitive data like API keys and passwords
							</p>
						</div>
						<button
							onclick={() => (showSecretModal = true)}
							class="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
						>
							Add Secret
						</button>
					</div>
				</div>
				<div class="divide-y divide-gray-200 dark:divide-gray-700">
					{#each secrets as secret}
						<div class="p-6">
							<div class="flex items-start justify-between">
								<div class="flex-1">
									<div class="flex items-center gap-2">
										<svg class="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
											<path
												fill-rule="evenodd"
												d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
												clip-rule="evenodd"
											/>
										</svg>
										<h3 class="font-medium text-gray-900 dark:text-gray-100">{secret.name}</h3>
									</div>
									<p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
										Created {secret.createdAt} • Updated {secret.updatedAt}
									</p>
								</div>
								<div class="flex items-center gap-2">
									<button
										onclick={() => openUpdateSecretModal(secret)}
										class="px-3 py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
									>
										Update
									</button>
									<button
										onclick={() => deleteSecret(secret.id)}
										class="text-red-600 hover:text-red-700 dark:text-red-400"
									>
										<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
											<path
												fill-rule="evenodd"
												d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
												clip-rule="evenodd"
											/>
										</svg>
									</button>
								</div>
							</div>
						</div>
					{/each}
				</div>
			</div>
		{:else if selectedSection === 'usage'}
			<!-- Usage Statistics -->
			<div class="space-y-6">
				<!-- Storage Usage -->
				<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
					<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Storage Usage</h2>
					<div class="space-y-4">
						<div>
							<div class="flex items-center justify-between mb-2">
								<span class="text-sm text-gray-600 dark:text-gray-400">Repository Size</span>
								<span class="text-sm font-medium text-gray-900 dark:text-gray-100">
									{usageStats.storageUsed} MB / {usageStats.storageLimit} MB
								</span>
							</div>
							<div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
								<div
									class="bg-orange-600 h-2 rounded-full transition-all"
									style="width: {(usageStats.storageUsed / usageStats.storageLimit) * 100}%"
								></div>
							</div>
						</div>
					</div>
				</div>

				<!-- Bandwidth Usage -->
				<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
					<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
						Bandwidth Usage (This Month)
					</h2>
					<div class="space-y-4">
						<div>
							<div class="flex items-center justify-between mb-2">
								<span class="text-sm text-gray-600 dark:text-gray-400">Data Transfer</span>
								<span class="text-sm font-medium text-gray-900 dark:text-gray-100">
									{usageStats.bandwidthUsed} GB / {usageStats.bandwidthLimit} GB
								</span>
							</div>
							<div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
								<div
									class="bg-blue-600 h-2 rounded-full transition-all"
									style="width: {(usageStats.bandwidthUsed / usageStats.bandwidthLimit) * 100}%"
								></div>
							</div>
						</div>
					</div>
				</div>

				<!-- Usage Breakdown -->
				<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
					<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Usage Breakdown</h2>
					<div class="space-y-3">
						<div class="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
							<span class="text-gray-700 dark:text-gray-300">Git LFS Storage</span>
							<span class="font-medium text-gray-900 dark:text-gray-100">
								{usageStats.gitLfsStorage} MB
							</span>
						</div>
						<div class="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
							<span class="text-gray-700 dark:text-gray-300">Code Repository</span>
							<span class="font-medium text-gray-900 dark:text-gray-100">
								{usageStats.codeRepository} MB
							</span>
						</div>
						<div class="flex items-center justify-between py-2">
							<span class="text-gray-700 dark:text-gray-300">Build Artifacts</span>
							<span class="font-medium text-gray-900 dark:text-gray-100">
								{usageStats.buildArtifacts} MB
							</span>
						</div>
					</div>
				</div>
			</div>
		{:else if selectedSection === 'danger'}
			<!-- Danger Zone -->
			<div class="bg-white dark:bg-gray-800 rounded-lg border-2 border-red-300 dark:border-red-800">
				<div class="p-6 border-b border-red-200 dark:border-red-900">
					<h2 class="text-xl font-semibold text-red-600 dark:text-red-400">Danger Zone</h2>
					<p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
						Irreversible and destructive actions
					</p>
				</div>
				<div class="p-6 space-y-6">
					<!-- Archive Repository -->
					<div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-gray-200 dark:border-gray-700">
						<div>
							<h3 class="font-medium text-gray-900 dark:text-gray-100">Archive this repository</h3>
							<p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
								Mark this repository as archived and read-only. It will no longer accept contributions.
							</p>
						</div>
						<button
							onclick={archiveRepository}
							class="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors whitespace-nowrap"
						>
							Archive Repository
						</button>
					</div>

					<!-- Transfer Ownership -->
					<div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-gray-200 dark:border-gray-700">
						<div>
							<h3 class="font-medium text-gray-900 dark:text-gray-100">
								Transfer repository ownership
							</h3>
							<p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
								Transfer this repository to another user or organization.
							</p>
						</div>
						<button
							onclick={transferRepository}
							class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
						>
							Transfer Repository
						</button>
					</div>

					<!-- Delete Repository -->
					<div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div>
							<h3 class="font-medium text-gray-900 dark:text-gray-100">Delete this repository</h3>
							<p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
								Once you delete a repository, there is no going back. Please be certain.
							</p>
						</div>
						<button
							onclick={deleteRepository}
							class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors whitespace-nowrap"
						>
							Delete Repository
						</button>
					</div>
				</div>
			</div>
		{/if}
	</div>
</div>

<!-- Modals -->
{#if showTokenModal}
	<div
		class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
		onclick={() => (showTokenModal = false)}
	>
		<div
			class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6"
			onclick={(e) => e.stopPropagation()}
		>
			<h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Generate Access Token</h3>
			<div class="space-y-4">
				<div>
					<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Token Name
					</label>
					<input
						type="text"
						bind:value={newTokenName}
						placeholder="e.g., CI/CD Pipeline"
						class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
					/>
				</div>
				<div>
					<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Scopes
					</label>
					<div class="space-y-2">
						{#each availableScopes as scope}
							<label class="flex items-center gap-2">
								<input
									type="checkbox"
									value={scope.value}
									onchange={(e) => {
										if (e.currentTarget.checked) {
											selectedScopes = [...selectedScopes, scope.value];
										} else {
											selectedScopes = selectedScopes.filter((s) => s !== scope.value);
										}
									}}
								/>
								<span class="text-sm text-gray-700 dark:text-gray-300">{scope.label}</span>
							</label>
						{/each}
					</div>
				</div>
				<div class="flex gap-2 pt-4">
					<button
						onclick={generateToken}
						class="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
					>
						Generate
					</button>
					<button
						onclick={() => (showTokenModal = false)}
						class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
					>
						Cancel
					</button>
				</div>
			</div>
		</div>
	</div>
{/if}

{#if showWebhookModal}
	<div
		class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
		onclick={() => {
			showWebhookModal = false;
			newWebhookUrl = '';
			selectedEvents = [];
		}}
	>
		<div
			class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6"
			onclick={(e) => e.stopPropagation()}
		>
			<h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Add Webhook</h3>
			<div class="space-y-4">
				<div>
					<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Payload URL
					</label>
					<input
						type="url"
						bind:value={newWebhookUrl}
						placeholder="https://api.example.com/webhook"
						class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
					/>
				</div>
				<div>
					<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Events
					</label>
					<div class="space-y-2 max-h-40 overflow-y-auto">
						{#each availableEvents as event}
							<label class="flex items-center gap-2">
								<input
									type="checkbox"
									value={event.value}
									onchange={(e) => {
										if (e.currentTarget.checked) {
											selectedEvents = [...selectedEvents, event.value];
										} else {
											selectedEvents = selectedEvents.filter((ev) => ev !== event.value);
										}
									}}
								/>
								<span class="text-sm text-gray-700 dark:text-gray-300">{event.label}</span>
							</label>
						{/each}
					</div>
				</div>
				<div class="flex gap-2 pt-4">
					<button
						onclick={addWebhook}
						disabled={!newWebhookUrl || selectedEvents.length === 0}
						class="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					>
						Add Webhook
					</button>
					<button
						onclick={() => {
							showWebhookModal = false;
							newWebhookUrl = '';
							selectedEvents = [];
						}}
						class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
					>
						Cancel
					</button>
				</div>
			</div>
		</div>
	</div>
{/if}

{#if showMemberModal}
	<div
		class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
		onclick={() => (showMemberModal = false)}
	>
		<div
			class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6"
			onclick={(e) => e.stopPropagation()}
		>
			<h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Add Member</h3>
			<div class="space-y-4">
				<div>
					<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Email Address
					</label>
					<input
						type="email"
						bind:value={newMemberEmail}
						placeholder="user@example.com"
						class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
					/>
				</div>
				<div>
					<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Role
					</label>
					<select
						bind:value={newMemberRole}
						class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
					>
						<option value="read">Read - Can view and clone</option>
						<option value="write">Write - Can push to repository</option>
						<option value="admin">Admin - Full repository access</option>
					</select>
				</div>
				<div class="flex gap-2 pt-4">
					<button
						onclick={addMember}
						class="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
					>
						Add Member
					</button>
					<button
						onclick={() => (showMemberModal = false)}
						class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
					>
						Cancel
					</button>
				</div>
			</div>
		</div>
	</div>
{/if}

{#if showSecretModal}
	<div
		class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
		onclick={() => {
			showSecretModal = false;
			newSecretName = '';
			newSecretValue = '';
			isEditingSecret = false;
			editingSecretId = null;
		}}
	>
		<div
			class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6"
			onclick={(e) => e.stopPropagation()}
		>
			<h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
				{isEditingSecret ? 'Update Secret' : 'Add Secret'}
			</h3>
			<div class="space-y-4">
				<div>
					<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Secret Name
					</label>
					<input
						type="text"
						bind:value={newSecretName}
						disabled={isEditingSecret}
						placeholder="e.g., AWS_ACCESS_KEY"
						class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-60 disabled:cursor-not-allowed"
					/>
					{#if isEditingSecret}
						<p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
							Secret name cannot be changed
						</p>
					{/if}
				</div>
				<div>
					<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{isEditingSecret ? 'New Secret Value' : 'Secret Value'}
					</label>
					<textarea
						bind:value={newSecretValue}
						placeholder={isEditingSecret ? 'Enter new secret value...' : 'Enter secret value...'}
						rows="4"
						class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
					></textarea>
					<p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
						Secrets are encrypted and cannot be viewed after {isEditingSecret ? 'updating' : 'creation'}
					</p>
				</div>
				<div class="flex gap-2 pt-4">
					<button
						onclick={isEditingSecret ? updateSecret : addSecret}
						disabled={!newSecretValue || (!isEditingSecret && !newSecretName)}
						class="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isEditingSecret ? 'Update Secret' : 'Add Secret'}
					</button>
					<button
						onclick={() => {
							showSecretModal = false;
							newSecretName = '';
							newSecretValue = '';
							isEditingSecret = false;
							editingSecretId = null;
						}}
						class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
					>
						Cancel
					</button>
				</div>
			</div>
		</div>
	</div>
{/if}
